import React, { useCallback, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';
import { isEmpty, map } from 'lodash';

import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { Box, IconButton, Stack, Typography } from '@mui/material';

import {
  useGetAssetsPictures,
  useGetFieldsCategory,
  useGetInternalAttributes,
} from 'src/pages/ProjectDetailsPage/api';
import { locale } from 'src/config';
import { colors } from 'src/utils/customColors';
import { forceString } from 'src/utils/datetime';
import { formatNumber } from 'src/utils/standards';
import { ImageGallery } from 'src/components/ImageGallery';
import { useDraggable } from 'src/utils/hooks/useDraggable';
import useClickOutside from 'src/utils/hooks/useClickOutside';
import { useGetCandidateDetails } from 'src/pages/CandidatesDetailsPage/api';

import messages from './messages';

const overflowSx = { overflowX: 'auto' };

interface Props {
  candidate?: string;
  handleClose: () => void;
}

export default function StudentDetails({ candidate, handleClose }: Props) {
  const { formatMessage } = useIntl();

  const ref = useRef<HTMLDivElement>(null);

  useClickOutside({ ref, callback: handleClose });

  const { position, handleMouseDown } = useDraggable({ ref });

  const { data: user, isPending } = useGetCandidateDetails({
    uuid: candidate,
    enabled: Boolean(candidate),
  });

  const { data: pictures } = useGetAssetsPictures({
    uuid: user?.data?.id,
    enabled: Boolean(user?.data?.id),
  });

  const { data: fieldsCategory } = useGetFieldsCategory({
    enabled: Boolean(candidate),
  });

  const { data: internalAttribures, isPending: pendingFields } =
    useGetInternalAttributes({
      userUuid: candidate,
      fields: fieldsCategory!,
      enabled: Boolean(fieldsCategory) && Boolean(candidate) && Boolean(user),
    });

  const status = useMemo(() => {
    switch (user?.data?.status?.code) {
      case 'completed':
        return { Icon: CheckOutlinedIcon, color: colors.lightGreen };
      default:
        return { Icon: null, color: colors.darkerBlue };
    }
  }, [user?.data?.status?.code]);

  // eslint-disable-next-line
  const resolveUserDynamicField = useCallback((dynamicField: any) => {
    if (!dynamicField) return null;
    const { label, description, values, type } = dynamicField;
    const value = () => {
      if (!Array.isArray(values) || values.length === 0) return '-';

      switch (type.code) {
        case 'date':
          return (
            map(values, ({ answer, option }) => {
              const formattedString = forceString(answer);
              return formattedString === 'Invalid date' || !formattedString
                ? answer || option?.inputValue
                : formattedString;
            })
              .filter((a) => a)
              .join(', ') || '-'
          );
        case 'multi-selectable':
        case 'single-selectable':
          return (
            map(values, ({ option, answer }) => option?.inputValue || answer)
              .filter((a) => a)
              .join(', ') || '-'
          );
        case 'number':
          return (
            map(values, ({ answer, option }) => {
              const formattedString = formatNumber(answer, locale);
              return formattedString === 'NaN' || !formattedString
                ? answer || option?.inputValue
                : formattedString;
            })
              .filter((a) => a)
              .join(', ') || '-'
          );
        case 'boolean':
          return (
            map(values, ({ answer, option }) => {
              if (answer === 'true') return formatMessage(messages.yesLabel);
              if (answer === 'false') return formatMessage(messages.noLabel);
              return answer || option?.inputValue;
            })
              .filter((a) => a)
              .join(', ') || '-'
          );
        default:
          return (
            map(values, ({ answer, option }) => answer || option?.inputValue)
              .filter((a) => a)
              .join(', ') || '-'
          );
      }
    };

    return (
      <Stack flex={1} display="flex">
        <Typography variant="subtitle2" color="textSecondary">
          {label}:
        </Typography>

        <Typography
          variant="subtitle2"
          color="textPrimary"
          textTransform="capitalize"
        >
          {value()}
        </Typography>

        {description && (
          <Typography variant="subtitle2">{description}</Typography>
        )}
      </Stack>
    );
  }, []);

  const renderLoader = () => {
    return (
      <Box
        flex={1}
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress size={20} />
      </Box>
    );
  };

  const { Icon, color } = status;

  return (
    <Paper
      ref={ref}
      sx={(theme) => ({
        zIndex: 10,
        top: position.y,
        left: position.x,
        position: 'fixed',
        height: theme.spacing(55),
        width: theme.spacing(56.25),
      })}
    >
      {isPending || pendingFields ? (
        renderLoader()
      ) : (
        <Stack height="100%">
          <Grid
            py={1}
            px={1.5}
            container
            alignItems="center"
            justifyContent="space-between"
            onMouseDown={handleMouseDown}
            sx={(theme) => ({
              cursor: 'move',
              border: `thin solid ${theme.palette.divider}`,
            })}
          >
            <Typography variant="h5">
              {formatMessage(messages.studentDetailsLabel)}
            </Typography>

            <IconButton onClick={handleClose}>
              <CloseOutlinedIcon />
            </IconButton>
          </Grid>

          <Grid flex={1} container p={1.5} sx={overflowSx}>
            <ImageGallery pictures={pictures} />

            <Stack gap={0.5}>
              <Typography variant="subtitle1">
                {user?.data?.id} - {user?.data?.firstName}
                {user?.data?.lastName}
              </Typography>

              <Grid container alignItems="center" gap={0.5}>
                {Icon && (
                  <Icon
                    sx={(theme) => ({
                      color,
                      width: theme.spacing(2.5),
                      height: theme.spacing(2.5),
                    })}
                  />
                )}

                <Typography variant="subtitle2" color={color}>
                  {user?.data?.status.name}
                </Typography>
              </Grid>

              <Stack gap={0.5} mt={1}>
                <Typography variant="subtitle2">
                  {formatMessage(messages.ageLabel, {
                    age: user?.data?.age ?? '-',
                  })}
                </Typography>

                <Typography variant="subtitle2">
                  {formatMessage(messages.ratingLabel, {
                    rating: user?.data?.rating ?? '-',
                  })}
                </Typography>

                <Typography variant="subtitle2">
                  {/* TODO - validate with backend */}
                  {formatMessage(messages.preScreenScoreLabel, {
                    score: user?.data?.rating ?? '-',
                  })}
                </Typography>
              </Stack>

              <Stack gap={0.5} mt={1}>
                <Typography variant="subtitle2">{user?.data?.email}</Typography>
              </Stack>

              <Grid container gap={0.5} mt={1} alignItems="center">
                {user?.data?.userLanguages.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <Typography
                      variant="subtitle2"
                      color={colors.greenSecondary}
                      sx={{ textDecoration: 'underline' }}
                    >
                      {item.language.name}
                    </Typography>

                    {index !== user?.data?.userLanguages.length - 1 && (
                      <Box
                        borderRadius="100%"
                        bgcolor={colors.mediumGrey}
                        width={(theme) => theme.spacing(0.5)}
                        height={(theme) => theme.spacing(0.5)}
                      />
                    )}
                  </React.Fragment>
                ))}
              </Grid>
            </Stack>

            {!isEmpty(internalAttribures) && (
              <Stack sx={overflowSx} p={1.5} pt={2.5}>
                <Grid
                  p={1.5}
                  container
                  spacing={2}
                  width="100%"
                  border="thin solid"
                  borderRadius={0.5}
                  borderColor={(theme) => theme.palette.grey[500]}
                >
                  {map(internalAttribures, (dynamicField) => (
                    <Grid key={dynamicField?.id} size={{ xs: 12, md: 6 }}>
                      {resolveUserDynamicField(dynamicField)}
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            )}
          </Grid>
        </Stack>
      )}
    </Paper>
  );
}
